// @flow
import React, { Fragment } from 'react';

import Link from 'app/common/components/Link';
import help from 'app/developer-v3/components/PartnerSupportForm/components/blocks/Help';
import radio from 'app/developer-v3/components/PartnerSupportForm/components/blocks/Radio';
import {
  BASE_DOC_URL_VB,
  PARTNER_SOLUTIONS_BASE_DOC_URL,
} from 'app/developer-v3/constants';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import {
  getUserFields,
  messageField,
} from 'app/developer-v3/components/PartnerSupportForm/content/nodes/common';

export const createPublishingYourApp = (
  isLoggedIn: boolean,
  newOrExistingApp: string
) => ({
  value: 'applying',
  label: 'Applying for public activation',
  zendeskCustomFieldValue: `pcf-${newOrExistingApp}-activation`,
  child: radio({
    id: 'applying',
    label: "Let's get you some help",
    // These selections are repeated in both new app or existing app
    // form selection paths. To DRY things up, change the 'id' and 'value'
    // based on if they chose new or existing app.
    zendeskCustomFieldId:
      newOrExistingApp === 'new' ? 360044559672 : 360044625731,
    items: [
      {
        value: `${newOrExistingApp}-process-applying`,
        label: 'How do I publish my app publicly on Zapier?',
        zendeskCustomFieldValue: `pcf-${newOrExistingApp}-publish-how-publicly`,
        // Don't show this option if app selected is already public
        shouldRender: (context: { [prop: string]: any }) =>
          !context.selectedApp ||
          (context.selectedApp && !context.selectedApp.isPublic),
        child: help({
          docs: {
            title: 'Applying for your App to go public',
            body: () => (
              <Fragment>
                <p>
                  Read our{' '}
                  <Link
                    href={`${BASE_DOC_URL_VB}/publish/integration-publishing-requirements`}
                    target="_blank"
                  >
                    App Review Guidelines
                  </Link>{' '}
                  to help you better prepare your integration before submitting
                  it for review.
                </p>
                <p>
                  After reviewing the guidelines, open your integration and
                  click the Publish button. Expect to hear from us within a
                  week.
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/publish/public-integration#4-submit-your-integration-for-app-review`,
                title: 'Submit your integration for review by the Zapier team',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about applying for public activation.`,
            },
          ],
        }),
      },
      {
        value: `${newOrExistingApp}-activation-review`,
        label: "Question on my app's technical review",
        zendeskCustomFieldValue: `pcf-${newOrExistingApp}-publish-technical-review`,
        // Don't show this option if app selected is already public
        shouldRender: (context: { [prop: string]: any }) =>
          !context.selectedApp ||
          (context.selectedApp && !context.selectedApp.isPublic),
        child: help({
          docs: {
            title: 'App Integration Review Criteria',
            body: () => (
              <Fragment>
                <p>
                  It’s important to become familiar with the criteria Zapier
                  uses to review all new app integrations.
                </p>
                <p>
                  View our{' '}
                  <Link
                    href={`${BASE_DOC_URL_VB}/publish/integration-build-guidelines`}
                    target="_blank"
                  >
                    integration tips
                  </Link>
                  .
                </p>
                <p>
                  If you already have a thread discussing questions with us,
                  please let us know - otherwise please continue to write in.
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/publish/integration-publishing-requirements`,
                title: 'Integration publishing requirements',
              },
              {
                href: `${BASE_DOC_URL_VB}/publish/branding-guidelines`,
                title: 'Integration branding and design guidelines',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about your App's activation review.`,
            },
          ],
        }),
      },
      {
        value: 'beta-launch',
        label: 'Post-publish: Zapier beta & launch',
        zendeskCustomFieldValue: `pcf-${newOrExistingApp}-publish-post-publish`,
        child: help({
          docs: {
            title: 'Zapier beta & launch',
            // TODO: get bold to take effect in <p> below.
            body: () => (
              <Fragment>
                <p>
                  Apps begin their public life on Zapier as Beta, after clearing
                  app review. During this time our system checks integrations
                  daily to ensure they remain healthy, and that essential launch
                  materials like Zap templates have been created. Once all the
                  requirements are met, you will be automatically enrolled in
                  the launch queue and notified of our co-marketing.
                </p>
                <p>
                  After launch, you will automatically join the{' '}
                  <b>Zapier Partner Program</b> and can access the program’s{' '}
                  <Link
                    href={`${ZAPIER_APP_BASE_URL}/developer-platform/partner-program`}
                    target="_blank"
                  >
                    many co-marketing benefits
                  </Link>
                  .
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${BASE_DOC_URL_VB}/publish/partner-program`,
                title: 'About the Partner Program',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about Zapier Beta & Launch.`,
            },
          ],
        }),
      },
      {
        value: 'embedding',
        label: 'Launch prep: embedding Zapier within my product',
        zendeskCustomFieldValue: `pcf-${newOrExistingApp}-publish-launch-prep`,
        child: help({
          docs: {
            title: 'Embedding Zapier within my Product',
            body: () => (
              <Fragment>
                <p>
                  Embedding is one of the number one activities that help
                  generate active users for our partner’s integrations. We have
                  several tools and links to help you embed.
                </p>
                <p>
                  {' '}
                  First step is to make sure your app have been approved and is
                  publicly available on Zapier’s marketplace. Second is to
                  create your{' '}
                  <Link
                    href={`${BASE_DOC_URL_VB}/publish/zap-templates`}
                    target="_blank"
                  >
                    Zap templates.
                  </Link>{' '}
                  Once these are approved and live on your Zapier listing, then
                  you can embed them within your product.
                </p>
              </Fragment>
            ),
            links: [
              {
                href: `${PARTNER_SOLUTIONS_BASE_DOC_URL}/integration-marketplace/low-code/zap-templates`,
                title: 'Zap templates',
              },
              {
                href: `${PARTNER_SOLUTIONS_BASE_DOC_URL}/integration-marketplace/low-code/workflow-element`,
                title: 'Full Zapier Experience',
              },
              {
                href: `${PARTNER_SOLUTIONS_BASE_DOC_URL}/built-in-workflows/low-code/embed-zap-editor`,
                title: 'Embed Zap editor',
              },
              {
                href: `${PARTNER_SOLUTIONS_BASE_DOC_URL}/built-in-workflows/low-code/pre-filled-zaps`,
                title: 'Pre-filled Zap workflows',
              },
              {
                href: `${PARTNER_SOLUTIONS_BASE_DOC_URL}/workflow-api/intro`,
                title: 'Workflow API',
              },
              {
                href: `${PARTNER_SOLUTIONS_BASE_DOC_URL}/managed-authentication/quick-account-creation`,
                title: 'Quick Account Creation',
              },
            ],
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about embedding Zapier within your product.`,
            },
          ],
        }),
      },
      {
        value: 'beta-tag',
        label: 'Launch wrap-up: removing Beta tag',
        zendeskCustomFieldValue: `pcf-${newOrExistingApp}-publish-launch-wrap-up`,
        child: help({
          docs: {
            title: 'Removing Beta Tag',
            body: () => (
              <Fragment>
                <p>
                  Your integration will remain in beta for 90 days. Once 90 days
                  have passed, you will automatically enter our Partner Program
                  and your integration will become eligible to earn marketing
                  and support benefits. It is also possible to exit Beta early
                  by embedding your integration. Learn more about the beta phase
                  and program benefits{' '}
                  <Link
                    href={`${BASE_DOC_URL_VB}/publish/public-integration#5-beta-phase`}
                    target="_blank"
                  >
                    here
                  </Link>
                  .
                </p>
              </Fragment>
            ),
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              details: `Please let us know what questions you have about removing the Beta tag.`,
            },
          ],
        }),
      },
    ],
  }),
});
